import React from 'react';

import { getGeoRegionsAsync, IGeoRegion } from '@apps/Routes/@services/geo.services';
import PageLoader from '@components/PageLoader';
import PohAlert, { AlertVariant } from '@ui-kit/Alert';
import PohButton, { ButtonVariant } from '@ui-kit/Button';
import PohCheckbox from '@ui-kit/Checkbox';
import PohInput, { InputTypes } from '@ui-kit/Input';
import PohList from '@ui-kit/List';
import { showModal } from '@ui-kit/Modal';

interface IGeoRegionSelectorProps {
  everyoneIsNobody?: boolean;
  multiple: boolean;
  selectedIds?: IGeoRegion['id'][];
  onSelect: (selected: IGeoRegion[]) => void;
}

const GeoRegionSelector: React.FC<IGeoRegionSelectorProps> = ({
  multiple,
  onSelect,
  selectedIds,
  everyoneIsNobody,
}) => {
  const [q, setQ] = React.useState('');
  const [selected, setSelected] = React.useState<number[]>(selectedIds ?? []);
  const [geoRegions, setGeoRegions] = React.useState<IGeoRegion[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [err, setErr] = React.useState<Error>();

  React.useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        const typesData = await getGeoRegionsAsync();
        setGeoRegions(typesData);
      } catch (e) {
        setErr(e as Error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  if (isLoading) return <PageLoader />;
  if (err) return <PohAlert variant={AlertVariant.Danger}>{err.message}</PohAlert>;

  return (
    <div>
      <div className="d-flex align-content-center">
        <PohInput
          className="mb-2"
          type={InputTypes.search}
          value={q}
          onChange={setQ}
          placeholder="Найти"
        />
      </div>
      {multiple && (
        <div className="text-end">
          <small>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelected(
                  selected.length === geoRegions.length ? [] : geoRegions.map((x) => x.id),
                );
              }}
            >
              {selected.length === geoRegions.length ? 'Снять выделение' : 'Выбрать все'}
            </a>
          </small>
        </div>
      )}
      <div style={{ overflowY: 'auto', minWidth: 400, maxHeight: 300 }}>
        <PohList
          data={geoRegions
            .filter((s) => !q.length || s.name.toLowerCase().includes(q.toLowerCase()))
            .map((geo) => ({
              key: geo.id,
              content: (
                <div className="d-flex align-items-baseline" style={{ gap: 6, cursor: 'pointer' }}>
                  {multiple && <PohCheckbox checked={selected.includes(geo.id)} />}
                  {geo.name}
                </div>
              ),
              active: !multiple && selected.includes(geo.id),
              action: !multiple,
              onClick: multiple
                ? () => {
                    setSelected((c) =>
                      !c.includes(geo.id) ? [...c, geo.id] : c.filter((f) => f !== geo.id),
                    );
                  }
                : () => onSelect(selected.includes(geo.id) ? [] : [geo]),
            }))}
        />
      </div>
      {multiple && (
        <footer className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
          <small className="text-muted">Выбрано: {selected.length}</small>

          <PohButton
            variant={selected.length ? ButtonVariant.Primary : undefined}
            onClick={() =>
              onSelect(
                everyoneIsNobody && selected.length === geoRegions.length
                  ? []
                  : geoRegions.filter((t) => selected.includes(t.id)),
              )
            }
          >
            Сохранить
          </PohButton>
        </footer>
      )}
    </div>
  );
};

export function selectGeoRegionOneAsync({
  selected,
}: {
  selected?: IGeoRegion['id'];
}): Promise<IGeoRegion | null> {
  return new Promise((resolve) => {
    showModal({
      size: 'sm',
      title: 'Выбор региона',
      children: (closeHandler: () => void) => (
        <GeoRegionSelector
          multiple={false}
          selectedIds={selected ? [selected] : undefined}
          onSelect={([s]) => {
            resolve(s ?? null);
            closeHandler();
          }}
        />
      ),
    });
  });
}

export function selectGeoRegionsSeveralAsync({
  selected,
  everyoneIsNobody,
}: {
  selected?: IGeoRegion['id'][];
  everyoneIsNobody?: boolean;
}): Promise<IGeoRegion[]> {
  return new Promise((resolve) => {
    showModal({
      size: 'sm',
      title: 'Выбор регионов',
      children: (closeHandler: () => void) => (
        <GeoRegionSelector
          multiple={true}
          selectedIds={selected}
          everyoneIsNobody={everyoneIsNobody}
          onSelect={(s) => {
            resolve(s);
            closeHandler();
          }}
        />
      ),
    });
  });
}
